import {Trans} from '@lingui/macro';
import {partition, sortBy} from 'lodash';
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {HandlerOf, PredicateOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {styled} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ResourceMap} from '../../../store/appTypes';
import {ViewingNowCalendar} from '../../../store/panels/calendars/calendarsPanelState';
import {CalendarsPanelId} from '../../../store/panels/panelStateBase';
import {CalendarsListItem} from './calendarsListItem';
import {ViewingNowCalendarsListItem} from './viewingNowCalendarsListItem';

/*
 * Props.
 */

interface CalendarsListProps {
  panelId: CalendarsPanelId;
  findCalendarVersionName: (calendarVersion: CalendarVersionModel) => string;
  findIsCalendarVersionImporting: PredicateOf<number>;
  findIsCalendarVersionSelected: PredicateOf<number>;
  sortedCalendarVersions: ReadonlyArray<CalendarVersionModel>;
  onCalendarVersionClick: HandlerOf<number>;
  shouldCombineCalendarsList: boolean;
  calendarVersions: ResourceMap<CalendarVersionModel>;
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>;
  onViewingNowCalendarsChange: HandlerOf<ReadonlyArray<ViewingNowCalendar>>;
  onAddCalendarClick: HandlerOf<number>;
  onRemoveCalendarClick: HandlerOf<number>;
  unselectCalendarVersion: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledSectionNameDiv = styled.div`
  padding: 0 16px;
  color: ${(p) => p.theme.header};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

const StyledMyCalendarsSectionNameDiv = styled(StyledSectionNameDiv)`
  margin-top: 10px;
  margin-bottom: 6px;
`;

const StyledViewingCalendarsSectionNameDiv = styled(StyledSectionNameDiv)`
  display: grid;
  grid-auto-columns: 1fr auto;
  grid-auto-flow: column;
  align-items: center;
  margin-top: 4px;
`;

const StyledSeparatorDiv = styled.div`
  height: 1px;
  margin: 6px 16px;
  background-color: ${(p) => p.theme.greys.shade40};
`;

const StyledViewingNowCalendarsListItemWrapper = styled.div`
  margin: 0 10px;
`;

const StyledClearAllButton = styled(Button)`
  left: 10px;
`;

/*
 * Component.
 */

export const CalendarsList: FC<CalendarsListProps> = (props) => {
  const {sortedCalendarVersions, viewingNowCalendars, onViewingNowCalendarsChange} = props;
  const [sortOrder, setSortOrder] = useState<ReadonlyArray<number>>([]);

  const resortedCalendarVersions = useMemo(
    () => sortBy(sortedCalendarVersions, (v) => sortOrder.indexOf(v.id)),
    [sortedCalendarVersions, sortOrder],
  );

  /*
   * Freeze list order on mouse enter.
   */

  const mouseLeaveTimeoutRef = useRef<number>();
  useEffect(() => () => globalWindow.clearTimeout(mouseLeaveTimeoutRef.current), []);

  const onMouseLeave = () => {
    mouseLeaveTimeoutRef.current = globalWindow.setInstrumentedTimeout(() => {
      setSortOrder([]);
    }, 1000);
  };

  const onMouseEnter = () => {
    globalWindow.clearTimeout(mouseLeaveTimeoutRef.current);
    setSortOrder(sortedCalendarVersions.map((v) => v.id));
  };

  const onRemoveViewingNowCalendar = useCallback(
    (calendar: ViewingNowCalendar) => {
      const newViewingNowCalendars = viewingNowCalendars.filter((c) => c.address !== calendar.address);
      onViewingNowCalendarsChange(newViewingNowCalendars);
    },
    [viewingNowCalendars, onViewingNowCalendarsChange],
  );

  if (props.shouldCombineCalendarsList) {
    // Render the combined list.
    return renderCombinedLists(props, onRemoveViewingNowCalendar);
  }

  return (
    <>
      {maybeRenderViewingNowCalendars(props, onRemoveViewingNowCalendar)}
      <div onMouseLeave={onMouseLeave} onMouseEnter={onMouseEnter}>
        {renderCalendars(props, resortedCalendarVersions)}
      </div>
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderViewingNowCalendars(
  props: CalendarsListProps,
  onRemoveViewingNowCalendar: HandlerOf<ViewingNowCalendar>,
) {
  if (props.viewingNowCalendars.length === 0) {
    // No header is needed for combined lists or if this is empty.
    return null;
  }

  return (
    <>
      <StyledViewingCalendarsSectionNameDiv>
        <Trans id="viewingnow-header">Viewing now</Trans>
        <StyledClearAllButton
          variant={ButtonVariantsEnum.LINK}
          size={VisualSizesEnum.SMALL}
          onClick={() => props.onViewingNowCalendarsChange([])}
        >
          <Trans id="viewingnow-clear-all">Clear all</Trans>
        </StyledClearAllButton>
      </StyledViewingCalendarsSectionNameDiv>
      {props.viewingNowCalendars.map((viewingNowCalendar) => (
        <StyledViewingNowCalendarsListItemWrapper>
          <ViewingNowCalendarsListItem
            key={viewingNowCalendar.address}
            viewingNowCalendar={viewingNowCalendar}
            onRemoveViewingNowCalendar={onRemoveViewingNowCalendar}
            calendarVersions={props.calendarVersions}
            onAddCalendarClick={props.onAddCalendarClick}
          />
        </StyledViewingNowCalendarsListItemWrapper>
      ))}
    </>
  );
}

function renderCalendars(props: CalendarsListProps, calendarVersions: ReadonlyArray<CalendarVersionModel>) {
  const [primaryCalendars, teammateCalendars] = partition(
    calendarVersions,
    (calendarVersion) => calendarVersion.isPrimary,
  );
  const shouldRenderSeparator = primaryCalendars.length > 0 && teammateCalendars.length > 0;

  return (
    <>
      <StyledMyCalendarsSectionNameDiv>
        <Trans id="calendar-list-calendars">Calendars</Trans>
      </StyledMyCalendarsSectionNameDiv>
      {primaryCalendars.map((calendarVersion) => renderCalendarsRow(props, calendarVersion))}
      {shouldRenderSeparator && <StyledSeparatorDiv />}
      {teammateCalendars.map((calendarVersion) => renderCalendarsRow(props, calendarVersion))}
    </>
  );
}

function renderCalendarsRow(props: CalendarsListProps, calendarVersion: CalendarVersionModel) {
  return (
    <CalendarsListItem
      key={calendarVersion.id}
      panelId={props.panelId}
      calendar={{calendarVersion}}
      calendarName={props.findCalendarVersionName(calendarVersion)}
      isImporting={props.findIsCalendarVersionImporting(calendarVersion.id)}
      isSelected={props.findIsCalendarVersionSelected(calendarVersion.id)}
      onClick={() => {
        props.onCalendarVersionClick(calendarVersion.id);
        trackInterfaceEvent(InterfaceEventsEnum.TOGGLE_CALENDAR);
      }}
      isMoreEnabled
      onRemoveCalendarClick={(calendarVersionId) => {
        if (calendarVersion.isSelected) {
          props.unselectCalendarVersion(calendarVersionId);
        }
        props.onRemoveCalendarClick(calendarVersionId);
      }}
    />
  );
}

function renderCombinedLists(
  props: CalendarsListProps,
  onRemoveViewingNowCalendar: HandlerOf<ViewingNowCalendar>,
) {
  const {sortedCalendarVersions, viewingNowCalendars} = props;
  const [calendarVersions, moreCalendarVersions] = partition(sortedCalendarVersions, (calendarVersion) =>
    props.findIsCalendarVersionSelected(calendarVersion.id),
  );

  return (
    <>
      {calendarVersions.map((calendarVersion) => (
        <CalendarsListItem
          key={calendarVersion.id}
          calendarName={props.findCalendarVersionName(calendarVersion)}
          calendar={{calendarVersion}}
          isSelected={props.findIsCalendarVersionSelected(calendarVersion.id)}
          onClick={() => props.onCalendarVersionClick(calendarVersion.id)}
          panelId={props.panelId}
          isImporting={props.findIsCalendarVersionImporting(calendarVersion.id)}
        />
      ))}
      {viewingNowCalendars.map((viewingNowCalendar) => (
        <CalendarsListItem
          key={viewingNowCalendar.address}
          calendarName={viewingNowCalendar.address}
          calendar={{viewingNowCalendar}}
          isSelected
          onClick={() => onRemoveViewingNowCalendar(viewingNowCalendar)}
          panelId={props.panelId}
          isImporting={false}
        />
      ))}
      {calendarVersions.length + viewingNowCalendars.length > 0 && moreCalendarVersions.length > 0 && (
        <StyledSeparatorDiv />
      )}
      {moreCalendarVersions.map((calendarVersion) => (
        <CalendarsListItem
          key={calendarVersion.id}
          calendarName={props.findCalendarVersionName(calendarVersion)}
          calendar={{calendarVersion}}
          isSelected={props.findIsCalendarVersionSelected(calendarVersion.id)}
          onClick={() => props.onCalendarVersionClick(calendarVersion.id)}
          panelId={props.panelId}
          isImporting={props.findIsCalendarVersionImporting(calendarVersion.id)}
        />
      ))}
    </>
  );
}
